import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { ColorType, getFontColor } from "../../../configs/constants/ColorType";
import { DATE_TIME_FORMAT_TYPES } from "../../../configs/constants/DateTimeFormatTypes";
import { TIME_REPORT_STATUS } from "../../../configs/constants/TimeReportStatus";
import TimeReport from "../../../models/TimeReport";
import AppIcon, { AppIconType } from "../../Core/AppIcon/AppIcon";
import AppText from "../../Core/AppText/AppText";
import Button from "../../Core/Button/Button";
import Flex from "../../Layout/Flex";
import ActionsPopOver, { PopOverAction } from "../ActionsPopover/ActionsPopover";

interface Props {
	onAction: (o: { status?: TIME_REPORT_STATUS; publishToCustomer?: boolean }) => void;
	timeReport: TimeReport;
}

const TimeReportHistory: React.FC<Props> = ({ timeReport, onAction }) => {
	const { t } = useTranslation();

	const getMetadataString = (
		changedAt?: Date,
		changedBy?: { firstName?: string; lastName?: string }
	) => {
		if (!changedAt) return t("PENDING");

		let metadataString = `${moment(changedAt).format(DATE_TIME_FORMAT_TYPES.DATE)}`;

		if (changedBy) {
			metadataString += ` ${t("BY")}`;

			if (changedBy.firstName) metadataString += ` ${changedBy.firstName}`;

			if (changedBy.lastName) metadataString += ` ${changedBy.lastName}`;
		}

		return metadataString;
	};

	const { metadata } = timeReport;

	function getCustomerAttestRow() {
		if (timeReport.status === TIME_REPORT_STATUS.DECLINED_BY_CUSTOMER) {
			const actions: PopOverAction[] = [
				{
					text: t("RESOLVE"),
					action: () => onAction({ status: TIME_REPORT_STATUS.RESOLVED_DECLINE_BY_ADMIN }),
				},
			];

			return (
				<TimeReportHistoryRow
					row1={t("CUSTOMER_ATTEST") + ": " + t("DECLINED")}
					row2={getMetadataString(metadata.customerAttestedAt, metadata.customerAttestedBy)}
					color={"danger"}
					icon="white-cross"
					iconSize="22px"
					showBtn={true}
					button={() => <ActionsPopOver actions={actions} />}
				/>
			);
		} else if (timeReport.status === TIME_REPORT_STATUS.RESOLVED_DECLINE_BY_ADMIN) {
			const actions: PopOverAction[] = [
				{
					text: t("timeReports.undoResolve"),
					textStyles: {
						color: "danger",
					},
					action: () => onAction({ status: TIME_REPORT_STATUS.DECLINED_BY_CUSTOMER }),
				},
			];

			return (
				<TimeReportHistoryRow
					row1={t("CUSTOMER_ATTEST") + ": " + t("RESOLVED")}
					row2={getMetadataString(metadata.resolvedDeclineAt, metadata.resolvedDeclineBy)}
					color={"actionBlue"}
					showBtn={true}
					button={() => <ActionsPopOver actions={actions} />}
				/>
			);
		} else {
			const actions: PopOverAction[] = [
				{
					text: t("ATTEST"),
					textStyles: {
						color: "green",
					},
					action: () => onAction({ status: TIME_REPORT_STATUS.ATTESTED_BY_CUSTOMER }),
				},
				{
					text: t("DECLINE"),
					textStyles: {
						color: "danger",
					},
					action: () => onAction({ status: TIME_REPORT_STATUS.DECLINED_BY_CUSTOMER }),
				},
			];

			if (!timeReport.isPublishedToCustomer) {
				actions.push({
					text: t("timeReports.publish"),
					action: () => onAction({ publishToCustomer: true }),
				});
			}

			return (
				<TimeReportHistoryRow
					row1={t("CUSTOMER_ATTEST")}
					row2={
						timeReport.isPublishedToCustomer
							? getMetadataString(
									metadata.customerAttestedAt || metadata.silentAttestedAt,
									metadata.customerAttestedBy
							  )
							: t("timeReports.pendingPublish")
					}
					color={metadata.customerAttestedAt || metadata.silentAttestedAt ? "green" : "dividerGray"}
					showBtn={timeReport.status === TIME_REPORT_STATUS.ATTESTED_BY_ADMIN}
					onSubmit={() => onAction({ status: TIME_REPORT_STATUS.ATTESTED_BY_CUSTOMER })}
					button={() => <ActionsPopOver actions={actions} />}
				/>
			);
		}
	}

	return (
		<Styles>
			<TimeReportHistoryRow
				row1={t("REPORTED")}
				row2={getMetadataString(metadata.reportedAt, metadata.reportedBy)}
				color={metadata.reportedAt ? "green" : "dividerGray"}
				buttonTitle={t("ATTEST")}
				showBtn={
					timeReport.status === TIME_REPORT_STATUS.PENDING &&
					moment(timeReport.shift.end).isBefore(new Date())
				}
				onSubmit={() => onAction({ status: TIME_REPORT_STATUS.WORKED })}
			/>

			<TimeReportHistoryRow
				row1={t("INTERNAL_ATTEST")}
				row2={getMetadataString(metadata.adminAttestedAt, metadata.adminAttestedBy)}
				color={metadata.adminAttestedAt ? "green" : "dividerGray"}
				buttonTitle={t("ATTEST")}
				showBtn={timeReport.status === TIME_REPORT_STATUS.WORKED}
				onSubmit={() => onAction({ status: TIME_REPORT_STATUS.ATTESTED_BY_ADMIN })}
			/>

			{getCustomerAttestRow()}

			<TimeReportHistoryRow
				row1={t("INVOICED")}
				color={!!timeReport.invoiceBatch ? "green" : "dividerGray"}
				row2={""}
			/>
		</Styles>
	);
};

export default TimeReportHistory;

const Styles = styled.div`
	.time-report-history-row {
		margin: 1rem 0;
		display: flex;
	}
`;

const Marker = styled.div<{ statusColor?: ColorType }>`
	background-color: ${(props) =>
		props.statusColor
			? getFontColor(props.statusColor, props.theme)
			: props.theme.colors.INPUT_BORDER};
	border-radius: 50%;
	min-width: 31px;
	max-width: 31px;
	max-height: 31px;
	min-height: 31px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const TimeReportHistoryRow: React.FC<{
	color?: ColorType;
	row1: string;
	row2: string;
	buttonTitle?: string;
	showBtn?: boolean;
	icon?: AppIconType;
	iconSize?: string;
	onSubmit?: () => any;
	button?: React.FC;
}> = ({
	row1,
	row2,
	buttonTitle,
	color,
	showBtn,
	onSubmit,
	icon = "white-check",
	iconSize = "14px",
	button: CustomButton,
}) => {
	return (
		<div className="time-report-history-row">
			<Marker statusColor={color}>
				<AppIcon icon={icon} size={iconSize} />
			</Marker>
			<Flex
				direction="row"
				margin="0 0 0 0.8rem"
				justifyContent="space-between"
				alignItems="center"
			>
				<Flex direction="column" maxWidth="190px">
					<AppText margin="0 0 0.15rem 0">{row1}</AppText>
					<AppText size="s" color="grayText" lineClamp={1}>
						{row2}
					</AppText>
				</Flex>
				{showBtn &&
					(CustomButton ? (
						<CustomButton />
					) : (
						<Button height="29px" onClick={onSubmit}>
							{buttonTitle}
						</Button>
					))}
			</Flex>
		</div>
	);
};
